import { useEffect, useRef, useState } from 'react'
import { useAuthSession } from '../../lib/authSession'
import BookWorkContent from '../rkdecks/BookWorkContent'
import { fetchBookWork, type BookWorkResponse } from '../rkdecks/bookWorkApi'
import type { ViewScope } from '../navigation/context'
import { ErrorState } from '../state'
import type { RecoveryProposalGroup } from './recovery'

/** Every exact discovered group stays separate; action reads re-admit the selection. */
export default function RecoveryBookReviews({
  groups,
  scope,
  onChanged,
  onPendingChange,
}: {
  groups: readonly RecoveryProposalGroup[]
  scope: ViewScope
  onChanged: () => void
  onPendingChange?: (pending: boolean) => void
}) {
  const session = useAuthSession()
  const pending = useRef(new Set<string>())
  const reportPending = (key: string, active: boolean) => {
    if (active) pending.current.add(key)
    else pending.current.delete(key)
    onPendingChange?.(pending.current.size > 0)
  }
  // The existing Book read accepts up to fifty IDs. Keep every legitimate
  // selection reachable without changing its action/bundle admission contract.
  return (
    <div className="context-workbench__book-reviews" data-testid="recovery-book-reviews">
      {groups.flatMap((group) =>
        Array.from({ length: Math.ceil(group.proposal_ids.length / 50) }, (_, index) => (
          <GroupReview
            key={`${session}:${group.group_id}:${index}:${group.proposal_ids.join(',')}`}
            groupId={group.group_id}
            proposalIds={group.proposal_ids.slice(index * 50, (index + 1) * 50)}
            scope={scope}
            onChanged={onChanged}
            onPendingChange={(active) => reportPending(`${group.group_id}:${index}`, active)}
          />
        ))
      )}
    </div>
  )
}
function GroupReview({
  groupId,
  proposalIds,
  scope,
  onChanged,
  onPendingChange,
}: {
  groupId: string
  proposalIds: readonly string[]
  scope: ViewScope
  onChanged: () => void
  onPendingChange: (pending: boolean) => void
}) {
  const [read, setRead] = useState<BookWorkResponse | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [refresh, setRefresh] = useState(0)
  const proposalKey = JSON.stringify(proposalIds)
  const fundId = scope.fund_id ?? null,
    clientId = scope.fund_id ? null : (scope.client_id ?? null)
  useEffect(() => {
    let active = true
    setRead(null)
    setError(null)
    const reviewScope = { fundId, clientId }
    fetchBookWork(groupId, JSON.parse(proposalKey) as string[], reviewScope)
      .then((value) => {
        if (active) setRead(value)
      })
      .catch((failure: unknown) => {
        if (active) setError(failure)
      })
    return () => {
      active = false
    }
  }, [groupId, proposalKey, fundId, clientId, refresh])
  if (error)
    return (
      <ErrorState
        compact
        what="Book review"
        error={error}
        onRetry={() => setRefresh((n) => n + 1)}
      />
    )
  if (!read) return <p role="status">Reading the selected Book work…</p>
  return (
    <BookWorkContent
      groupId={groupId}
      work={read}
      onPendingChange={onPendingChange}
      onApplied={onChanged}
      onRefresh={() => {
        setRefresh((n) => n + 1)
        onChanged()
      }}
    />
  )
}
